import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TIER_META, TRANSFER_AUDIT_META } from "@/lib/images";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fadeUp = { initial: { opacity: 0, y: 14 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5 } };

const FAQ = [
  { q: "What if I'm only in my first semester?", a: "That's the best time to start. The most important admissions decisions are made in your first two semesters — which classes, which professors, which activities." },
  { q: "Do I need a 4.0?", a: "No. We work with students from ~3.4 and up. The Selective & Elite tier is reserved for students whose academic record is already competitive for top-50 transfers." },
  { q: "Can I upgrade tiers later?", a: "Yes. You only pay the delta. Many clients start on Platform or with a Transfer Audit and upgrade to Florida Transfer or Selective & Elite once their narrative is clearer." },
  { q: "Do you work with non-Florida community colleges?", a: "Yes. The methodology is general. Florida is our depth specialty, not our scope." },
  { q: "Refund policy?", a: "Platform is non-refundable after 7 days. Florida Transfer and Selective & Elite engagements have a milestone-based refund structure. The Transfer Audit is delivered on a fixed timeline and is non-refundable once the audit call is held." },
];

export default function Pricing() {
  const [busy, setBusy] = useState(null);
  const [tiersFromApi, setTiersFromApi] = useState(null);
  const [credit, setCredit] = useState(null); // {balance_cents, balance_display} | null
  useEffect(() => { (async () => {
    try { const res = await api.get("/tiers"); const map = {}; (res.data.tiers || []).forEach((t) => (map[t.id] = t)); setTiersFromApi(map); } catch {}
    try {
      const r = await api.get("/portal/referrals/summary");
      if (r.data?.credit_balance_cents > 0) {
        setCredit({ balance_cents: r.data.credit_balance_cents, balance_display: r.data.credit_balance_display });
      }
    } catch { /* anon user — no credit banner */ }
    const hash = window.location.hash?.slice(1);
    if (hash) requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  })(); }, []);

  const buy = async (id) => {
    setBusy(id);
    try {
      const res = await api.post("/checkout/session", { tier_id: id, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Stripe did not return a checkout URL");
    } catch { toast.error("Failed to start checkout"); }
    finally { setBusy(null); }
  };
  const tiersList = Object.values(TIER_META);

  return (
    <Layout>
      <SEO
        title="Pricing — Platform, Florida Transfer, Selective & Elite, Transfer Audit"
        description="Four levels of engagement: $99 Platform, $1,500 Florida Transfer, $4,500 Selective & Elite, plus the $249 Transfer Audit. Fixed pricing. No surprises."
        canonical="/pricing"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Beyond Valencia — Florida Transfer Admissions Consulting",
            provider: { "@type": "Organization", name: "Beyond Valencia" },
            areaServed: { "@type": "State", name: "Florida" },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Beyond Valencia Tiers",
              itemListElement: [
                { "@type": "Offer", name: "Platform", price: 99, priceCurrency: "USD" },
                { "@type": "Offer", name: "Florida Transfer", price: 1500, priceCurrency: "USD" },
                { "@type": "Offer", name: "Selective & Elite Transfer", price: 4500, priceCurrency: "USD" },
                { "@type": "Offer", name: "Transfer Audit", price: 249, priceCurrency: "USD" },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="pricing-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16">
          <Eyebrow>Pricing</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.75rem] text-ink leading-[1.04] tracking-tightest mt-5">Choose your level<br /><span className="italic text-ivy">of foresight.</span></h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">The level of engagement should match the thought you&rsquo;ve put into what comes next.</p>
          {credit && (
            <div className="mt-8 inline-flex items-center gap-3 border border-ivy bg-ivy text-bone py-3 px-5" data-testid="pricing-credit-banner">
              <span className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Credit available</span>
              <span className="font-serif text-xl tabular-nums" data-testid="pricing-credit-amount">{credit.balance_display}</span>
              <span className="text-xs text-bone-dim/80">— applied automatically to Florida Transfer or Selective &amp; Elite at checkout.</span>
            </div>
          )}
        </div>
      </section>

      {/* Front of funnel — Transfer Audit */}
      <section className="bg-bone border-b border-stone/30" id="transfer_audit">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="border border-stone/40 p-2 bg-bone-dim/40">
              <img src={TRANSFER_AUDIT_META.image} alt={TRANSFER_AUDIT_META.name} className="w-full aspect-[4/3] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brass">Front of funnel · One-time</div>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl text-ink mt-3 tracking-tight">{TRANSFER_AUDIT_META.name}</h2>
            <div className="text-ivy text-sm font-medium uppercase tracking-[0.18em] mt-3">{TRANSFER_AUDIT_META.priceLabel}</div>
            <p className="mt-5 text-ink-soft leading-relaxed">{TRANSFER_AUDIT_META.promise}</p>
            <ul className="mt-6 space-y-2 border-t border-stone/30 pt-5">
              {TRANSFER_AUDIT_META.bullets.map((b) => (<li key={b} className="flex items-start gap-3 text-sm text-ink"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span>{b}</span></li>))}
            </ul>
            <button onClick={() => buy("transfer_audit")} disabled={busy === "transfer_audit"} className="mt-8 bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors disabled:opacity-60" data-testid="pricing-buy-transfer_audit">
              {busy === "transfer_audit" ? "Redirecting…" : "Order Your Transfer Audit — $249"}
            </button>
          </div>
        </div>
      </section>

      {/* 3 ongoing tiers */}
      <section className="bg-bone">
        {tiersList.map((t, i) => {
          const apiTier = tiersFromApi?.[t.id];
          const reverse = i % 2 === 1;
          return (
            <motion.div key={t.id} id={t.id} {...fadeUp} className="scroll-mt-24 border-b border-stone/30" data-testid={`tier-section-${t.id}`}>
              <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="border border-stone/40 p-2 bg-bone-dim/40"><img src={t.image} alt={t.name} className="w-full aspect-[4/3] object-cover" /></div>
                </div>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="flex items-baseline gap-4"><span className="font-serif text-brass text-5xl leading-none">{t.numeral}</span><span className="text-[11px] uppercase tracking-[0.22em] text-stone">Tier {i + 1}</span></div>
                  <h2 className="font-serif font-medium text-3xl sm:text-4xl lg:text-5xl text-ink mt-4 leading-tight tracking-tight">{t.name}</h2>
                  <div className="text-ivy text-sm font-medium uppercase tracking-[0.18em] mt-3">{t.priceLabel}{apiTier && <span className="ml-3 text-stone normal-case tracking-normal font-normal">(live: ${apiTier.amount.toFixed(2)})</span>}</div>
                  <p className="mt-5 text-ink-soft leading-relaxed">{t.promise}</p>
                  <ul className="mt-6 space-y-2.5 border-t border-stone/30 pt-5">{t.bullets.map((b) => (<li key={b} className="flex items-start gap-3 text-sm text-ink"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span>{b}</span></li>))}</ul>
                  <div className="mt-7 border-l-2 border-brass pl-5"><div className="text-[11px] uppercase tracking-[0.22em] text-brass">Best for</div><p className="text-sm text-ink mt-1 leading-relaxed">{t.bestFor}</p></div>
                  <button onClick={() => buy(t.id)} disabled={busy === t.id} className="mt-8 bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors disabled:opacity-60" data-testid={`pricing-buy-${t.id}`}>
                    {busy === t.id ? "Redirecting…" : t.id === "platform" ? "Start with Platform — $99" : "Get started"}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-24">
        <SectionLabel index="•" label="Questions, honestly answered" />
        <Accordion type="single" collapsible className="mt-8 border-t border-stone/40" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-stone/40">
              <AccordionTrigger className="text-left text-ink font-medium hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-ink-soft leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </Layout>
  );
}
